import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCircleCheck, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmButtonImports, type ButtonVariants } from '@spartan-ng/helm/button';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';

@Component({
  selector: 'app-save-button',
  standalone: true,
  imports: [NgIcon, HlmButtonImports, HlmSpinnerImports],
  providers: [provideIcons({ lucideCircleCheck, lucideTriangleAlert })],
  templateUrl: './save-button.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SaveButtonComponent implements OnInit, OnDestroy {
  @Input() text: string = 'Speichern';
  @Input() loadingText: string = 'Speichern...';
  @Input() loading: boolean = false;
  @Input() disabled: boolean = false;
  @Input() type: 'button' | 'submit' = 'button';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() width: 'auto' | 'full' = 'auto';
  @Input() successMessage: string = 'Erfolgreich gespeichert';
  @Input() errorMessage: string = '';
  @Input() showSuccess: boolean = false;
  @Input() showError: boolean = false;
  @Input() autoHideSuccess: boolean = true;
  @Input() autoHideError: boolean = false;
  @Input() autoHideDelay: number = 3000;

  @Output() clickEvent = new EventEmitter<void>();
  @Output() successEvent = new EventEmitter<void>();
  @Output() errorEvent = new EventEmitter<string>();

  private successTimeout?: number;
  private errorTimeout?: number;

  ngOnInit() {
    if (this.showSuccess && this.autoHideSuccess) {
      this.scheduleHideSuccess();
    }
    if (this.showError && this.autoHideError) {
      this.scheduleHideError();
    }
  }

  ngOnDestroy() {
    if (this.successTimeout) {
      clearTimeout(this.successTimeout);
    }
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  }

  onClick() {
    if (!this.disabled && !this.loading) {
      this.clickEvent.emit();
    }
  }

  showSuccessMessage(message?: string) {
    this.showSuccess = true;
    if (message) {
      this.successMessage = message;
    }
    this.successEvent.emit();

    if (this.autoHideSuccess) {
      this.scheduleHideSuccess();
    }
  }

  showErrorMessage(message: string) {
    this.showError = true;
    this.errorMessage = message;
    this.errorEvent.emit(message);

    if (this.autoHideError) {
      this.scheduleHideError();
    }
  }

  hideMessages() {
    this.showSuccess = false;
    this.showError = false;
  }

  private scheduleHideSuccess() {
    if (this.successTimeout) {
      clearTimeout(this.successTimeout);
    }
    this.successTimeout = window.setTimeout(() => {
      this.showSuccess = false;
    }, this.autoHideDelay);
  }

  private scheduleHideError() {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
    this.errorTimeout = window.setTimeout(() => {
      this.showError = false;
    }, this.autoHideDelay);
  }

  /** Maps the component's own size scale onto the button variants. */
  get hlmSize(): ButtonVariants['size'] {
    switch (this.size) {
      case 'sm':
        return 'sm';
      case 'lg':
        return 'lg';
      default:
        return 'default';
    }
  }
}
